/**
 * ListMarkdownPreview (HS2-2N5183): a first-class, reusable rendered-Markdown
 * preview block for the click-to-edit fields (a demand's Description, a supporting
 * Note). It is a Kerf `ContentItem` (HS2-J5C8SH), which owns its geometry and its
 * hover/focus paint; `compact` maps to `flush`.
 *
 * Empty state shows a muted placeholder (`data-action="<emptyAction>"`); the filled
 * state carries `data-action="<action>"`. Either edits on a single click (HS2-61J7B5),
 * and an editable preview ends its last line with a subtle pencil hint. A click on a
 * link inside the rendered Markdown follows the link and never starts editing.
 * An interactive preview is an interactive ContentItem (`role="button"`, a Tab
 * stop): `wireContentItems` turns Enter/Space into the same click, while keys pressed
 * on a link inside the rendered Markdown stay with the link (HS2-YBZTVW).
 */
import './list-markdown-preview.css';

import { ContentItem } from '@kerfjs/ui/content-item';
import { uiColor } from '@kerfjs/ui/css-values';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Text } from '@kerfjs/ui/text';
import { raw, type SafeHtml } from 'kerfjs';
import { Pencil } from 'lucide';

import { renderMarkdown } from '../lib/markdown.js';

export interface ListMarkdownPreviewOpts {
  /** The Markdown source; HTML is escaped by renderMarkdown before formatting. */
  markdown: string;
  /** Placeholder text shown when the source is empty. */
  emptyLabel: string;
  /** data-action for the edit trigger in the filled state. Omit for a non-interactive,
   *  read-only preview (no data-action / role / tabindex) — e.g. a cancelled demand. */
  action?: string;
  /** data-action for the edit trigger in the empty state (defaults to `action`). */
  emptyAction?: string;
  /** Tooltip for the filled state (defaults to "Click or press Enter to edit"). */
  title?: string;
  /** Remove block-axis geometry for a tight preview below a ListHeader. */
  compact?: boolean;
}

/** The subtle edit hint at the end of an editable preview's last line (HS2-61J7B5). */
const EDIT_HINT_ICON = (
  <span class="list-md-edit-hint" aria-hidden="true">
    <LucideIcon icon={Pencil} name="" size="xs" color={uiColor('text-quiet')} />
  </span>
);

/** Blocks whose last line can carry the icon inline. */
const TEXT_BLOCKS = new Set(['p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'dd', 'dt']);
/** Blocks that cannot carry an inline icon; the hint follows them on its own line. */
const OPAQUE_BLOCKS = new Set(['pre', 'table']);

/**
 * Insert `icon` at the end of the rendered Markdown's last line: after any trailing
 * inline markup (so it never joins a link or emphasis) and inside the innermost text
 * block (a paragraph, heading, or the last list item). After a code block or table it
 * follows on its own line.
 */
export function withTrailingIcon(html: string, icon: string): string {
  const trimmed = html.trimEnd();
  const closers = /(?:<\/[a-z][a-z0-9]*>\s*)+$/i.exec(trimmed);
  if (closers === null) return `${trimmed}${icon}`;
  for (const match of closers[0].matchAll(/<\/([a-z][a-z0-9]*)>/gi)) {
    const tag = (match[1] ?? '').toLowerCase();
    if (OPAQUE_BLOCKS.has(tag)) return `${trimmed}<p>${icon}</p>`;
    if (TEXT_BLOCKS.has(tag)) {
      const at = closers.index + match.index;
      return `${trimmed.slice(0, at)}${icon}${trimmed.slice(at)}`;
    }
  }
  return `${trimmed}${icon}`;
}

export function ListMarkdownPreview({
  markdown,
  emptyLabel,
  action: filledAction,
  emptyAction,
  title,
  compact = false,
}: ListMarkdownPreviewOpts): SafeHtml {
  const md = markdown.trim();
  const empty = md === '';
  const action = empty ? (emptyAction ?? filledAction) : filledAction;
  const interactive = action !== undefined;
  const content = (
    <div class="list-md-body">
      {empty ? (
        <Text variant="span" tone="quiet" size="compact">
          {emptyLabel}
          {interactive ? EDIT_HINT_ICON : null}
        </Text>
      ) : (
        // eslint-disable-next-line kerfjs/no-raw-with-dynamic-arg
        raw(interactive ? withTrailingIcon(renderMarkdown(md), String(EDIT_HINT_ICON)) : renderMarkdown(md))
      )}
    </div>
  );
  return interactive ? (
    <ContentItem
      interactive
      action={action}
      flush={compact}
      className="list-markdown-preview"
      rootAttributes={{ 'data-empty': String(empty) }}
      title={empty ? emptyLabel : (title ?? 'Click or press Enter to edit')}
      shape="rounded"
    >
      {content}
    </ContentItem>
  ) : (
    <ContentItem
      flush={compact}
      className="list-markdown-preview"
      rootAttributes={{ 'data-empty': String(empty) }}
      title={empty ? emptyLabel : undefined}
    >
      {content}
    </ContentItem>
  );
}
